/**
 * Notice detail — R10. Kind pill, title, by + when, read count, body
 * paragraphs, attachment card (if attachmentName), Acknowledge action.
 * Wired: GET /api/notices/<id>, POST /api/notices/<id>/ack.
 * Port of NoticeDetailScreen in design/source/screens-notices.jsx.
 */
import { router, useLocalSearchParams } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { num, str } from '@/components/gate/api';
import { Icon } from '@/features/account-admin/icons';
import { ArchAvatar, BottomBar, goBack } from '@/features/community/bits';
import { timeAgo, useFetch } from '@/features/community/lib';
import { useT } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Button, Card, EmptyState, Pill, Pop, Screen, Skeleton, TopBar } from '@/ui';

type NoticeKind = 'urgent' | 'event' | 'committee' | 'general';
const KINDS: NoticeKind[] = ['urgent', 'event', 'committee', 'general'];

type NoticeDetail = {
  id: string; kind: NoticeKind; title: string; body: string; postedBy: string; when: string;
  readCount: number; ackCount: number; isAcknowledged: boolean; attachmentName: string | null;
};

function toDetail(raw: unknown): NoticeDetail | null {
  const id = str(raw, 'id');
  const title = str(raw, 'title');
  if (!id || !title) return null;
  const kindRaw = str(raw, 'kind') ?? 'general';
  const kind = (KINDS as string[]).includes(kindRaw) ? (kindRaw as NoticeKind) : 'general';
  const createdAt = str(raw, 'createdAt');
  const d = createdAt ? new Date(createdAt) : null;
  const isAck = !!(raw && typeof raw === 'object' && (raw as Record<string, unknown>).isAcknowledged === true);
  return {
    id, kind, title,
    body: str(raw, 'body') ?? '',
    postedBy: str(raw, 'postedBy') ?? 'Committee',
    when: d && !Number.isNaN(d.getTime()) ? timeAgo(d) : '',
    readCount: num(raw, 'readCount') ?? 0,
    ackCount: num(raw, 'ackCount') ?? 0,
    isAcknowledged: isAck,
    attachmentName: str(raw, 'attachmentName') ?? null,
  };
}

async function fetchNoticeDetail(id: string): Promise<NoticeDetail> {
  const body = await api.get<unknown>(`/api/notices/${id}`);
  const src = (body && typeof body === 'object' ? (body as Record<string, unknown>).notice ?? body : {});
  const n = toDetail(src);
  if (!n) throw new Error('Notice not found');
  return n;
}

const PILL_MAP: Record<NoticeKind, { status: 'urgent' | 'approved' | 'pending'; label: DictKey }> = {
  urgent: { status: 'urgent', label: 'pill_urgent' },
  event: { status: 'approved', label: 'pill_event' },
  committee: { status: 'approved', label: 'pill_committee' },
  general: { status: 'pending', label: 'pill_general' },
};

export default function NoticeDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { t } = useT();
  const { colors } = useTheme();
  const { data: notice, loading, error, refetch, mutate } = useFetch(() => fetchNoticeDetail(String(id)), [id]);

  const [acking, setAcking] = React.useState(false);
  const [ackError, setAckError] = React.useState<string | null>(null);

  const acknowledge = async () => {
    if (!notice || notice.isAcknowledged || acking) return;
    setAcking(true);
    setAckError(null);
    try {
      await api.post<{ ok: boolean; ackCount: number }>(`/api/notices/${notice.id}/ack`);
      mutate((prev) => (prev ? { ...prev, isAcknowledged: true, ackCount: prev.ackCount + 1 } : prev));
    } catch {
      setAckError(t('notice_ack_error'));
    } finally {
      setAcking(false);
    }
  };

  return (
    <View style={{ flex: 1, backgroundColor: colors.canvas0 }}>
      <TopBar title={t('notice_detail_title')} onBack={goBack} />
      {loading ? (
        <Screen padded style={{ paddingTop: space.s4 }}>
          <Skeleton w="30%" h={16} />
          <Skeleton w="90%" h={24} style={{ marginTop: space.s3 }} />
          <Skeleton w="60%" h={14} style={{ marginTop: space.s2 }} />
          <Skeleton h={14} style={{ marginTop: space.s6 }} />
          <Skeleton h={14} style={{ marginTop: space.s2 }} />
          <Skeleton w="70%" h={14} style={{ marginTop: space.s2 }} />
        </Screen>
      ) : error || !notice ? (
        <Screen padded>
          <EmptyState
            title={t('notice_unavailable_title')}
            body={t('notice_unavailable_body')}
            cta={t('retry')}
            onCta={() => void refetch()}
          />
        </Screen>
      ) : (
        <>
          {notice.kind === 'urgent' ? <View style={{ height: 6, backgroundColor: colors.accent500 }} /> : null}
          <Screen padded style={{ paddingTop: space.s4 }}>
            <Pill status={PILL_MAP[notice.kind].status} label={t(PILL_MAP[notice.kind].label)} />
            <AppText variant="headingLg" style={{ marginTop: space.s3 }}>{notice.title}</AppText>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2, marginTop: space.s3 }}>
              <ArchAvatar name={notice.postedBy} size={26} tone="primary" />
              <AppText variant="bodySm" tone="tertiary">
                {notice.postedBy}{notice.when ? ` · ${notice.when}` : ''}
              </AppText>
            </View>
            {notice.readCount > 0 ? (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 6 }}>
                <Icon name="info" size={14} color={colors.textSecondary} />
                <AppText variant="bodySm" tone="secondary">
                  {notice.readCount === 1
                    ? t('notice_read_by_one', { n: notice.readCount })
                    : t('notice_read_by_many', { n: notice.readCount })}
                </AppText>
              </View>
            ) : null}

            <View style={{ marginTop: space.s6 }}>
              {notice.body.split('\n\n').filter((p) => p.length > 0).map((p, i) => (
                <AppText key={i} variant="bodyLg" style={{ marginBottom: space.s4 }}>{p}</AppText>
              ))}
            </View>

            {notice.attachmentName ? (
              <View style={{ marginTop: space.s2 }}>
                <AppText variant="label" tone="tertiary" style={{ marginBottom: space.s3 }}>{t('notice_attachments')}</AppText>
                <Card
                  onPress={() => router.push(`/account/doc-viewer?name=${encodeURIComponent(notice.attachmentName ?? '')}`)}
                  style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}
                >
                  <Icon name="file-text" size={20} color={colors.textTertiary} />
                  <AppText variant="bodyMd" style={{ flex: 1 }} numberOfLines={1}>{notice.attachmentName}</AppText>
                  <AppText variant="bodyMd" weight="500" tone="brand">{t('notice_open')}</AppText>
                </Card>
              </View>
            ) : null}
            {ackError ? <AppText variant="bodySm" tone="danger" style={{ marginTop: space.s3 }}>{ackError}</AppText> : null}
          </Screen>

          <BottomBar>
            {notice.isAcknowledged ? (
              <Pop trigger={notice.isAcknowledged}>
              <View style={{
                minHeight: 44, borderRadius: 8, alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: 8,
                backgroundColor: colors.success50,
              }}>
                <Icon name="check" size={18} color={colors.success700} />
                <AppText variant="bodyMd" weight="500" style={{ color: colors.success700 }}>{t('ct_acknowledged')}</AppText>
              </View>
              </Pop>
            ) : (
              <Button title={t('acknowledge')} size="lg" loading={acking} onPress={() => void acknowledge()} />
            )}
          </BottomBar>
        </>
      )}
    </View>
  );
}
